'use strict';

const ACCOUNT_ASSIGNMENT_CHANGE_EVENT = 'accountAssignmentChanged'
window.ACCOUNT_ASSIGNMENT_CHANGE_EVENT = ACCOUNT_ASSIGNMENT_CHANGE_EVENT;

const ACCOUNTING_ENTRY_LIMIT = 500;

module.exports = /* @ngInject */ function ($resource, apiConfig) {

    const cachingInProgress = {
        costCenter: null,
        account: null,
        project: null,
        customAccountAssignment: null,
        customAccountAssignmentType: null,
    }

    let cache = {
        costCenter: null,
        account: null,
        project: null,
        customAccountAssignment: new Map(),
        customAccountAssignmentType: null,
    }

    // Search cache for v2 APIs (5 minutes cache duration)
    const searchCache = new Map();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    const COST_CENTER_URL = `${apiConfig.$get().apiUrl}/cost-center/list/active`;
    const COST_CENTER_SETTINGS_URL = `${apiConfig.$get().apiUrl}/cost-center/setting`;
    const COST_CENTER_SETTINGS_ADMIN_URL = `${apiConfig.$get().apiUrl}/cost-center/setting/customer?customerCompanyId=:customerCompanyId`;
    const PROJECT_URL = `${apiConfig.$get().apiUrl}/project/list/active`;
    const ACCOUNT_URL = `${apiConfig.$get().apiUrl}/account/user`;
    const CUSTOM_ACCOUNT_ASSIGNMENT_URL = `${apiConfig.$get().apiUrl}/custom-account-assignment/list/:type/active`;
    const CUSTOM_ACCOUNT_ASSIGNMENT_ACTIVE_IN_ORDER_URL = `${apiConfig.$get().apiUrl}/custom-account-assignment/type/active-in-order`;

    // New v2 API URLs for server-side search
    const COST_CENTER_SEARCH_URL = `${apiConfig.$get().apiUrl}/cost-center/v2/search`;
    const PROJECT_SEARCH_URL = `${apiConfig.$get().apiUrl}/project/v2/search`;
    const ACCOUNT_SEARCH_URL = `${apiConfig.$get().apiUrl}/general-ledger-account/v2/search`;
    const CUSTOM_ACCOUNT_SEARCH_URL = `${apiConfig.$get().apiUrl}/custom-account-assignment/:typeId/v2/search`;

    function init() {
        window.addEventListener(ACCOUNT_ASSIGNMENT_CHANGE_EVENT, (event) => {
            cache = {
                costCenter: null,
                account: null,
                project: null,
                customAccountAssignment: new Map(),
                customAccountAssignmentType: null,
            };
            // Clear search cache as well
            searchCache.clear();
        })
    }

    // Cache helper functions for search results
    function getCachedSearch(cacheKey) {
        const cached = searchCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
            return cached.data;
        }
        return null;
    }

    function setCachedSearch(cacheKey, data) {
        searchCache.set(cacheKey, {
            data: data,
            timestamp: Date.now()
        });
    }

    // Main search function for v2 APIs
    function searchAccountAssignments(url, query = '', include = null, companyId = null, companyCodeId = null) {
        return new Promise((resolve, reject) => {
            const params = {};
            if (query) params.query = query;
            if (include) params.include = include;
            if (companyId) params.companyId = companyId;
            if (companyCodeId) params.companyCodeId = companyCodeId;
            // Always limit results as per new requirement
            params.limit = ACCOUNTING_ENTRY_LIMIT;
            
            // Create cache key
            const cacheKey = `${url}_${JSON.stringify(params)}`;
            
            // Check cache first
            const cachedResult = getCachedSearch(cacheKey);
            if (cachedResult) {
                return resolve(cachedResult);
            }

            $resource(url, {}, {
                search: {
                    method: 'GET',
                    params: params,
                    isArray: true,
                    transformResponse: function(data) {
                        return data ? JSON.parse(data) : [];
                    }
                }
            }).search().$promise.then((data) => {
                // Cache the result
                setCachedSearch(cacheKey, data);
                resolve(data);
            }).catch(error => {
                console.error('Search failed:', error);
                // Fallback on empty array
                resolve([]);
            });
        });
    }

    // Main search function for v2 APIs
    function searchCustomAccountAssignments(typeId, query = '', include = null, companyId = null, companyCodeId = null) {
        return new Promise((resolve, reject) => {
            const url = CUSTOM_ACCOUNT_SEARCH_URL.replace(':typeId', typeId);
            const params = {};
            if (query) params.query = query;
            if (include) params.include = include;
            if (companyId) params.companyId = companyId;
            if (companyCodeId) params.companyCodeId = companyCodeId;
            // Always limit results as per new requirement
            params.limit = ACCOUNTING_ENTRY_LIMIT;

            // Create cache key
            const cacheKey = `${url}_${JSON.stringify(params)}`;

            // Check cache first
            const cachedResult = getCachedSearch(cacheKey);
            if (cachedResult) {
                return resolve(cachedResult);
            }

            $resource(url, {}, {
                search: {
                    method: 'GET',
                    params: params,
                    isArray: true,
                    transformResponse: function(data) {
                        const list = data ? JSON.parse(data) : [];
                        for(const entry of list) {
                            entry.type = typeId;
                        }

                        return list;
                    }
                }
            }).search().$promise.then((data) => {
                // Cache the result
                setCachedSearch(cacheKey, data);
                resolve(data);
            }).catch(error => {
                console.error('Search failed:', error);
                // Fallback on empty array
                resolve([]);
            });
        });
    }

    function getQueryOptions(cache, input) {
        return {
            query: {
                method: 'GET',
                transformResponse: function (data) {
                    if (data) {
                        const accountAssignments = sortAccountAssignments(JSON.parse(data));
                        if (cache instanceof Map) {
                            cache.set(input.type, accountAssignments);
                        } else {
                            cache = accountAssignments;
                        }
                        return accountAssignments;
                    } else {
                        return [];
                    }
                },
                isArray: true
            },
        }
    }

    function sortAccountAssignments(accountAssignmentArray) {
        return accountAssignmentArray.sort((accountAssignmentA, accountAssignmentB) => {
            if (accountAssignmentA.key > accountAssignmentB.key) {
                return 1;
            } else if (accountAssignmentA.key < accountAssignmentB.key) {
                return -1
            }

            return 0;
        })
    }

    function waitForCacheIfInProgress(cachingInProgressKey, callbackFn) {
        if (cachingInProgress[cachingInProgressKey]) {
            cachingInProgress[cachingInProgressKey].then(callbackFn);
        } else {
            cachingInProgress[cachingInProgressKey] = new Promise(resolveCachingProgress => {
                callbackFn(resolveCachingProgress);
            })

        }
    }

    function setCache(cacheKey, data, input) {
        if (cache[cacheKey] instanceof Map) {
            cache[cacheKey].set(input.type, data)
        } else {
            cache[cacheKey] = data;
        }
    }

    function fetchNonAccountAssignment(url, cacheKey, input = {}) {
        return new Promise((resolve, reject) => {
            waitForCacheIfInProgress(cacheKey, (resolveCachingProgress) => {
                if ((cache[cacheKey] instanceof Map && cache[cacheKey].has(input.type)) || (!(cache[cacheKey] instanceof Map) && cache[cacheKey])) {
                    return resolve(cache[cacheKey] instanceof Map ? cache[cacheKey].get(input.type) : cache[cacheKey]);
                } else {
                    $resource(
                        url,
                        {},
                        {
                            query: {
                                method: 'GET',
                            },
                        }
                    ).query(input).$promise.then((data) => {
                        setCache(cacheKey, data, input);
                        resolve(data);
                        if (resolveCachingProgress) {
                            resolveCachingProgress();
                        }
                    }, reject);
                }
            })
        });
    }

    function fetch(url, cacheKey, urlVariables = {}, input = {}, orderNumber = '', rfqNumber = '') {
        return new Promise((resolve, reject) => {
            waitForCacheIfInProgress(cacheKey, (resolveCachingProgress) => {
                if ((cache[cacheKey] instanceof Map && cache[cacheKey].has(input.type)) || (!(cache[cacheKey] instanceof Map) && cache[cacheKey])) {
                    return resolve(cache[cacheKey] instanceof Map ? cache[cacheKey].get(input.type) : cache[cacheKey]);
                } else {
                    if (orderNumber) {
                        url += `?orderNumber=${orderNumber}`;
                    } else if (rfqNumber) {
                        url += `?rfqNumber=${rfqNumber}`;
                    }

                    $resource(
                        url,
                        urlVariables,
                        getQueryOptions(cache, input)
                    ).query(input).$promise.then((data) => {
                        setCache(cacheKey, data, input);
                        resolve(data);
                        if (resolveCachingProgress) {
                            resolveCachingProgress();
                        }
                    }, reject);
                }
            })
        });
    }

    init();

    return {
        getCostCenters: (orderNumber, rfqNumber) => fetch(
            COST_CENTER_URL,
            `costCenter${orderNumber}${rfqNumber}`,
            {},
            {},
            orderNumber,
            rfqNumber),
        getCostCentersSettings: () => fetchNonAccountAssignment(
            COST_CENTER_SETTINGS_URL,
            `costCenterSettings`),
        getCostCentersSettingsAdmin: (customerCompanyId) => fetchNonAccountAssignment(
            COST_CENTER_SETTINGS_ADMIN_URL,
            `costCenterSettingsAdmin`, {customerCompanyId}),
        getAccounts: (orderNumber, rfqNumber) => fetch(
            ACCOUNT_URL,
            `account${orderNumber}${rfqNumber}`,
            {},
            {},
            orderNumber,
            rfqNumber),
        getProjects: (orderNumber, rfqNumber) => fetch(
            PROJECT_URL,
            `project${orderNumber}${rfqNumber}`,
            {},
            {},
            orderNumber,
            rfqNumber),
        getCustomAccountAssignments: (type, orderNumber, rfqNumber) => fetch(
            CUSTOM_ACCOUNT_ASSIGNMENT_URL,
            `customAccountAssignment${orderNumber}${rfqNumber}${type}`,
            {type: '@type'},
            {type},
            orderNumber,
            rfqNumber),
        getCustomAccountAssignmentActiveInOrder: (orderNumber, rfqNumber, customerCompanyId) => {
            // Build URL with appropriate query parameters, including optional customerCompanyId
            let url = CUSTOM_ACCOUNT_ASSIGNMENT_ACTIVE_IN_ORDER_URL;
            const params = [];
            if (orderNumber) {
                params.push(`orderNumber=${orderNumber}`);
            } else if (rfqNumber) {
                params.push(`rfqNumber=${rfqNumber}`);
            }
            if (customerCompanyId) {
                params.push(`customerCompanyId=${customerCompanyId}`);
            }
            if (params.length) {
                url += `?${params.join('&')}`;
            }

            // Include customerCompanyId in cache key to avoid cross-customer collisions
            const cacheKey = `customAccountAssignmentType${orderNumber || ''}${rfqNumber || ''}${customerCompanyId || ''}`;

            // Pass empty order/rfq to avoid fetch() appending duplicate query params
            return fetch(
                url,
                cacheKey,
                {},
                {},
                '',
                ''
            );
        },
        // New v2 search methods
        searchCostCenters: (query, include, companyId, companyCodeId) => searchAccountAssignments(COST_CENTER_SEARCH_URL, query, include, companyId, companyCodeId),
        searchProjects: (query, include, companyId, companyCodeId) => searchAccountAssignments(PROJECT_SEARCH_URL, query, include, companyId, companyCodeId),
        searchAccounts: (query, include, companyId, companyCodeId) => searchAccountAssignments(ACCOUNT_SEARCH_URL, query, include, companyId, companyCodeId),
        searchCustomAccountAssignments: (typeId, query, include, companyId, companyCodeId) => searchCustomAccountAssignments(typeId, query, include, companyId, companyCodeId),
        ACCOUNT_ASSIGNMENT_TYPES: {
            'ACCOUNT': 'ACCOUNT',
            'COST_CENTER': 'COST_CENTER',
            'PROJECT': 'PROJECT',
            'CUSTOM': 'CUSTOM',
        }

    }

}
